import { Component, inject, OnDestroy, OnInit, signal } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { MyMarketsService } from "./my-markets.service";
import { MarketService } from "../../../../../../shared/src/lib/services/market.service";
import { AsyncPipe } from "@angular/common";
import { MatExpansionModule } from "@angular/material/expansion";
import { BookmakerComponent } from "../market-layout/bookmaker/bookmaker.component";
import { FancyMarketComponent } from "../market-layout/fancy-market/fancy-market.component";
import { MarketOpsService } from "../market-layout/market/market-ops.service";
import { MarketComponent } from "../market-layout/market/market.component";
import { AlertComponent } from "../../../components/alert/alert.component";
import { LoaderService } from "../../../../../../shared/src/lib/services/loader.service";
import { ReactiveFormsModule } from "@angular/forms";
import { BookmakerMarket } from "../../../../../../shared/src/lib/types/bet.type";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-my-markets",
  imports: [MatExpansionModule, BookmakerComponent, FancyMarketComponent, MarketComponent, AlertComponent, AsyncPipe, ReactiveFormsModule],
  templateUrl: "./my-markets.component.html",
  styleUrl: "./my-markets.component.scss",
})
export class MyMarketsComponent implements OnInit, OnDestroy {
  readonly destroy$ = new Subject<void>();
  myMarketsService = inject(MyMarketsService);
  marketService = inject(MarketService);
  marketOps = inject(MarketOpsService);
  loader = inject(LoaderService);
  loading$!: Observable<boolean>;
  isFetchingStatus = signal(false);
  myMarkets$ = new BehaviorSubject<any[]>([]);

  ngOnInit() {
    this.loading$ = this.loader.getState();
    this.syncMyMarkets();
  }

  syncMyMarkets() {
    this.myMarketsService.myMarkets$.pipe(takeUntil(this.destroy$)).subscribe({
      next: (data) => {
        // console.log(data);
        if (data?.size) {
          const sbMarkets = Array.from(data.entries())
            .filter(([_, value]) => value?.descriptionPrefix && value.descriptionPrefix.trim() !== "")
            .map(([key]) => key);
          const bfMarkets = Array.from(data.entries())
            .filter(([_, value]) => !value?.descriptionPrefix)
            .map(([key]) => key);

          if (sbMarkets?.length) this.getSbMarkets(sbMarkets);
          if (bfMarkets?.length) this.getBfMarkets(bfMarkets);
          // console.log("SB Markets", sbMarkets);
          // console.log("BF Markets", bfMarkets);
        } else {
          this.myMarkets$.next([]);
        }
      },
    });
  }

  getSbMarkets(marketIds: string[]) {
    this.marketService
      .fetchSbMarkets({
        id: marketIds?.join(","),
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          // console.log(response);
          if (response?.data && response?.data.length) {
            const openMarkets = response?.data?.filter((m: any) => !["INACTIVE", "CLOSED"].includes(m?.status));
            this.setSbMarkets(openMarkets?.map((m: BookmakerMarket) => ({ ...m, key: `${m?.descriptionPrefix}_${m?.eventId}_${m?.providerId}` })));
          }
        },
      });
  }

  setSbMarkets(markets: any[]) {
    const topics = markets.map((m: any) => m?.topic);

    if (markets?.length) this.setMyMarkets(markets, { consolidationKey: "key" });
    this.marketOps.setTopics(topics, "merge");
  }

  // BF markets call
  getBfMarkets(marketIds: string[]) {
    this.isFetchingStatus.set(true);

    this.marketService
      .requestMarket({
        ids: marketIds,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          // console.log("BF Markets Response", response);
          if (response?.data && response?.data?.length) {
            const statusTopics = response?.data?.map((market: any) => `bf_${market?.marketId}`);
            const params = { market_ids: statusTopics?.join(",") };
            this.getBfMarketStatuses(params, response?.data);
          } else {
            this.isFetchingStatus.set(false);
          }
        },
      });
  }

  // BF markets status call
  getBfMarketStatuses(params: any, markets: any) {
    const qParams = { ...params, statuses: "2,3", return_snapshot: "Y" };

    this.marketService
      .fetchMarketStatus(qParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          // console.log("BF Markets Status Response", response);
          const { snapshot: snapshots = {}, statuses = {} } = response || {};
          const openedMarkets = statuses["2"] || [];
          const suspendedMarkets = statuses["3"] || [];
          const statusFilteredMarkets = markets
            .filter((market: any) => {
              const _marketId = `bf_${market?.marketId}`;
              const isMarketOpenOrSuspended = openedMarkets.includes(_marketId) || suspendedMarkets.includes(_marketId);
              if (!isMarketOpenOrSuspended) this.myMarketsService.removeOpenBetsMarket(market?.marketId);
              return isMarketOpenOrSuspended;
            })
            .map((market: any) => {
              const _marketId = `bf_${market?.marketId}`;
              return {
                ...market,
                mc: snapshots[_marketId],
                eventId: market?.event?.id,
                eventTypeId: market?.eventType?.id,
              };
            });

          this.setBetfairMarkets(statusFilteredMarkets);
        },
      });
  }

  setBetfairMarkets(markets: any[]) {
    // filter hidden sports, events, markets
    const topics = markets.map((m: any) => `bf_${m?.marketId}`);

    if (markets?.length) {
      this.setMyMarkets(markets, { consolidationKey: "marketId" });
    } else {
      this.isFetchingStatus.set(false);
    }
    this.marketOps.setTopics(topics, "merge");
  }

  setMyMarkets(markets: any[], config?: { consolidationKey: string }) {
    const currentMarketsFromSource = this.myMarketsService.myMarkets$.getValue();
    const currentMarkets = this.myMarkets$.getValue();
    const cKey = config?.consolidationKey;
    let updatedMarkets = [];
    if (cKey) {
      const currentMarketKeys = currentMarkets?.filter((m) => m?.[cKey])?.map((m) => m?.[cKey]);
      const consolidatedMarkets = markets?.filter((m) => !currentMarketKeys?.includes(m?.[cKey]));
      updatedMarkets = [...currentMarkets, ...consolidatedMarkets];
    } else {
      updatedMarkets = [...currentMarkets, ...markets];
    }

    // filter out markets that doesn't exist in the source of truth
    const filteredMarkets = updatedMarkets?.filter((m) => {
      const key = m?.key ?? m?.marketId;
      return currentMarketsFromSource?.has(key);
    });

    this.myMarkets$.next(filteredMarkets);
    this.isFetchingStatus.set(false);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
